import React, { useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from '@mui/styles'
import PerfectScrollbar from 'react-perfect-scrollbar'
import ThemeInterface from '@/theme-interface'
import lang from '@/lang'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import { dispatch } from '@/redux/ducks/lists'
import { setTab, updateDocument } from '@/redux/ducks/document/drawer'
import clsx from 'clsx'
import DocumentDateInput from '@/modules/document/components/file-info/fields/date-input'
import DocumentTextInput from '@/modules/document/components/file-info/fields/text-input'
import DocumentCurrencyInput from '@/modules/document/components/file-info/fields/currency-input'
import { bankDataByIBAN } from 'bankdata-germany'
import { useAppSelector } from '@/redux/store'
import checkOcrValues from '@/modules/document/_helpers/checkOcrValues'
// FLAG:useDocuAi — drawer-embedded central review (first review and re-review alike); an assigned
// revenue still routes to that revenue, see the redirect below.
import orgamax from 'ampersand-app'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { useNavigate } from 'react-router'
import revenueConfig from '@/modules/revenue/config'
import { isEmbeddedInvoiceReview } from '@/modules/document/utils/invoice-analysis-state'
import DocumentInvoiceReviewDrawerTab from '@/modules/document/components/review/document-invoice-review-drawer-tab'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  content: {
    padding: CssHelper.px(0, 30, 20),
    background: theme.white,
  },
  controls: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 20,
    '& label': {
      color: theme.labelLight,
      fontSize: '12px',
      fontWeight: 400,
      marginBottom: '10px',
      letterSpacing: '0.12px',
      height: '14px',
      display: 'block',
    },
  },
  manuallyPaid: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 24,
    '& label': {
      marginBottom: 0,
      marginRight: 20,
      color: theme.labelLight,
      display: 'block',
      fontSize: theme.fontSizes.small,
      fontWeight: 400,
      height: 21,
    },
  },
  row: {
    paddingTop: 20,
    display: 'flex',
    gap: 12,
    justifyContent: 'space-between',
  },
  one: {
    flexGrow: 1,
  },
  two: {
    width: '50%',
  },
  fill: {
    flexGrow: 1,
  },
  borderTop: {
    borderTop: theme.border,
    marginTop: 20,
  },
}))

// FLAG:useDocuAi — reviewSaveRef/refreshList wire the drawer-embedded review's save to the
// drawer footer; flag-OFF callers may omit them (legacy details form ignores both).
const IncomingInvoiceDetails = ({ reviewSaveRef, refreshList }: {
  reviewSaveRef?: React.Ref<{ onSave: () => void }>,
  refreshList?: () => void,
}) => {
  const classes = useStyles()
  const {
    document,
    folderReadOnly,
    activeTab,
  } = useAppSelector((state) => state.document.drawer)

  const {
    expense,
    revenue,
  } = document

  // FLAG:useDocuAi — the Details tab of an invoice with a review hosts the central review
  // component INSIDE the drawer (development parity: drawer stays open, no route
  // change — only the tab's content is the new shared form instead of the legacy fields).
  // This includes an invoice already assigned to an EXPENSE: that case used to redirect to
  // /expenses/edit/:id, which tore the drawer and the already-rendered PDF down to show a form the
  // drawer hosts itself. It is now re-reviewed in place (see isEmbeddedInvoiceReview).
  // flag-OFF: useDocuAiEnabled is false, so nothing embeds and the details tab renders exactly
  // as before.
  const navigate = useNavigate()
  const useDocuAiEnabled = orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  // This tab is always index 1 — the drawer builds [Allgemein, Details] for INCOMING_INVOICE
  // (document-drawer.tsx tabs construction).
  const detailsTabActive = activeTab === 1
  const embeddedReviewNow = !!useDocuAiEnabled && isEmbeddedInvoiceReview(document, folderReadOnly)
  // FLAG:useDocuAi — the drawer's `document` is continually REPLACED during analysis (every WS event
  // triggers a refetch). If `documentInvoiceDetails`
  // is briefly absent from one of these intermediate
  // versions, this predicate flips to false, the review component is DETACHED, and it remounts with
  // the next object — visible as a spinner flashing repeatedly during „Erneut analysieren".
  //
  // For the SAME document, the answer therefore remains fixed once determined: an existing review
  // does not disappear merely because an intermediate response omitted it. The memory is discarded
  // when the document changes; otherwise the drawer would show the review component for a receipt
  // that has none.
  const stickyReviewRef = useRef<{ documentId: unknown; value: boolean }>({ documentId: null, value: false })
  if (stickyReviewRef.current.documentId !== document?.id) {
    stickyReviewRef.current = { documentId: document?.id, value: embeddedReviewNow }
  } else if (embeddedReviewNow) {
    stickyReviewRef.current.value = true
  }
  const embeddedReview = embeddedReviewNow || stickyReviewRef.current.value

  // FLAG:useDocuAi — an assigned REVENUE keeps the pre-existing redirect. The drawer host can only
  // review in the expense direction (it mounts the workspace as RECEIPT_TYPES.EXPENSE), and there
  // is no createRevenueFromReviewedInvoice yet, so embedding a revenue here would review it in the
  // wrong booking direction — the same class of error the F18 cancellation gate prevents on the other
  // axis. Only while THIS tab is active: the drawer keeps every tab panel mounted (document-drawer
  // toggles visibility via CSS), so an on-mount redirect would fire the moment the drawer opens.
  const assignedRevenue = Array.isArray(revenue) ? revenue[0] : revenue
  const shouldRedirectToAssignedRevenue = !!useDocuAiEnabled && detailsTabActive && !!assignedRevenue?.id
  useEffect(() => {
    if (!shouldRedirectToAssignedRevenue) { return }
    // activeTab persists in the store across drawer close/open — without resetting to General,
    // returning from the revenue would immediately trigger the redirect again.
    dispatch(setTab(0))
    navigate(revenueConfig.routes.edit(assignedRevenue.id))
  }, [shouldRedirectToAssignedRevenue, assignedRevenue, navigate])

  // FLAG:useDocuAi — mount the embedded review lazily (first visit of the Details tab, so a
  // mere drawer open fetches nothing), but keep it mounted across tab switches afterwards:
  // unmounting would throw away the operator's entered form values (development parity —
  // the legacy tab kept its values across tab switches too). A document change resets it.
  const [reviewTabVisited, setReviewTabVisited] = useState(false)
  useEffect(() => {
    setReviewTabVisited(false)
  }, [document?.id])
  useEffect(() => {
    if (embeddedReview && detailsTabActive && !reviewTabVisited) {
      setReviewTabVisited(true)
    }
  }, [embeddedReview, detailsTabActive, reviewTabVisited])

  const ocrState = useMemo(() =>
    checkOcrValues(expense, revenue, document),
  [expense, revenue, document?.documentInvoiceDetails, document?.metaData?.ocr],
  )

  const {
    totalAmountValue,
    invoiceNumberValue,
    dateValue,
    dueDateValue,
    accountHolder,
    iban,
    bic,
    bankInstitute,
  } = ocrState.values

  const [bankInstituteFromIBAN, setBankInstituteFromIBAN] = useState('')
  const bicFromIBAN = iban && bankDataByIBAN(iban)?.bic

  useEffect(() => {
    const newIban = ocrState.values.iban || document?.metaData?.ocr?.payeeIban
    if (newIban) {
      const bankData = bankDataByIBAN(newIban)
      const newBankInstitute = bankData?.bankName || ''
      setBankInstituteFromIBAN(newBankInstitute)

      // Update document details with generated values.
      // FLAG:useDocuAi — never while the review owns these fields: this tab renders the review
      // component then, so the prefill would only dirty documentInvoiceDetails behind the
      // reviewer's back — enough to turn the footer's „Schließen" into „Speichern" and, before
      // saveDocument stopped carrying the field, to book an Ausgabe from an untouched drawer.
      if (!folderReadOnly && !embeddedReview && document.documentInvoiceDetails) {
        const updatedDetails = {
          ...document.documentInvoiceDetails,
          bic: bankData?.bic || document.documentInvoiceDetails.bic,
          bankInstitute: newBankInstitute || document.documentInvoiceDetails.bankInstitute,
        }
        dispatch(updateDocument({ key: 'documentInvoiceDetails', value: updatedDetails }))
      }
    }
  }, [ocrState.values.iban, document?.metaData?.ocr?.payeeIban, folderReadOnly, embeddedReview])

  // FLAG:useDocuAi — while redirecting to the assigned revenue (flag-ON only), render nothing.
  if (shouldRedirectToAssignedRevenue) {
    return null
  }

  // FLAG:useDocuAi — pending review: this tab's content IS the central review component
  // (mounted from the first visit on, see reviewTabVisited above).
  if (embeddedReview) {
    return (detailsTabActive || reviewTabVisited)
      ? <DocumentInvoiceReviewDrawerTab ref={reviewSaveRef} refreshList={refreshList} />
      : null
  }

  return (
    <PerfectScrollbar className={classes.content}>
      <div className={classes.controls}>
        <div className={classes.row}>
          <DocumentCurrencyInput
            isOcrValue={ocrState.priceTotal}
            label={lang.generics.totalAmount}
            name={'documentInvoiceDetails.totalAmount'}
            ocrKey={'priceTotal'}
            customClass={classes.one}
            value={totalAmountValue ?? document?.metaData?.ocr?.priceTotal ?? ''}
            readOnly={folderReadOnly}
          />
        </div>
        <div className={classes.row}>
          <DocumentTextInput
            isOcrValue={ocrState.receiptNumber}
            label={lang.documents.invoiceNumber}
            name={'documentInvoiceDetails.invoiceNumber'}
            ocrKey={'invoiceNumber'}
            customClass={classes.one}
            readOnly={folderReadOnly}
            value={
              invoiceNumberValue?.length > 0
                ? invoiceNumberValue
                : (document?.metaData?.ocr?.receiptNumber ?? '')
            }
          />
        </div>
        <div className={classes.row}>
          <DocumentDateInput
            isOcrValue={ocrState.date}
            label={lang.generics.invoiceDate}
            value={dateValue ?? document?.metaData?.ocr?.date ?? ''}
            name={'documentInvoiceDetails.date'}
            ocrKey={'date'}
            customClass={clsx(classes.two)}
            disabled={folderReadOnly}
          />
          <DocumentDateInput
            isOcrValue={ocrState.paymentDueDate}
            label={lang.generics.dueDate}
            value={dueDateValue ?? document?.metaData?.ocr?.paymentDueDate ?? ''}
            name={'documentInvoiceDetails.paymentDueDate'}
            ocrKey={'paymentDueDate'}
            customClass={clsx(classes.two)}
            disabled={folderReadOnly}
          />
        </div>
        { ((document.isAssigned && document?.state === 'paid') || !document.isAssigned) && (
          <div className={classes.manuallyPaid}>
            <DocumentDateInput
              isOcrValue={ocrState.payedAt}
              allowEdit
              label={lang.documents.manuallyPaidLabel}
              value={document?.documentInvoiceDetails?.payedAt ?? document?.metaData?.ocr?.payedAt ?? ''}
              name={'documentInvoiceDetails.payedAt'}
              ocrKey={'payedAt'}
              customClass={clsx(classes.fill)}
              disabled={folderReadOnly}
            />
          </div>
        )}
        <div className={clsx(classes.row, classes.borderTop)}>
          <DocumentTextInput
            isOcrValue={ocrState.accountHolder}
            hideEmpty
            label={lang.booking.bookingContactName}
            name={'documentInvoiceDetails.accountHolder'}
            ocrKey={'payee'}
            customClass={classes.one}
            readOnly={folderReadOnly}
            value={accountHolder ?? document?.metaData?.ocr?.payee ?? ''}
          />
        </div>
        <div className={classes.row}>
          <DocumentTextInput
            isOcrValue={ocrState.iban}
            hideEmpty
            label={lang.documentPaymentDrawer.labels.iban}
            name={'documentInvoiceDetails.iban'}
            ocrKey={'payeeIban'}
            customClass={classes.two}
            readOnly={folderReadOnly}
            value={iban ?? document?.metaData?.ocr?.payeeIban ?? ''}
          />
          <DocumentTextInput
            isOcrValue={ocrState.bic}
            hideEmpty
            label={lang.documentPaymentDrawer.labels.bicSummary}
            name={'documentInvoiceDetails.bic'}
            ocrKey={'payeeBic'}
            customClass={classes.two}
            readOnly={folderReadOnly}
            value={bic || bicFromIBAN || document?.metaData?.ocr?.payeeBic || ''}
          />
        </div>
        <div className={classes.row}>
          <DocumentTextInput
            isOcrValue={ocrState.bankInstitute}
            hideEmpty
            label={lang.documents.drawer.labels.bankInstitute}
            name={'documentInvoiceDetails.bankInstitute'}
            ocrKey={'payeeBank'}
            customClass={classes.one}
            readOnly={folderReadOnly}
            value={bankInstitute || bankInstituteFromIBAN || document?.metaData?.ocr?.payeeBank || ''}
          />
        </div>
      </div>
    </PerfectScrollbar>
  )
}

export default React.memo(IncomingInvoiceDetails)
